import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    Link,
    Typography,
} from '@mui/material';
import { displayValue, EmptyValue, formatDate, InfoRow, SectionCard } from './shared';
import { StatusChip } from '../../../components/ui';

export interface IWholesaleContact {
    fullName?: string;
    companyName?: string;
    email?: string;
    phone?: string;
    website?: string;
    bestReach?: string;
    howHeard?: string;
    shippingType?: string;
    address?: string;
    apartment?: string;
    city?: string;
    state?: string;
    zip?: string;
}

export interface IWholesaleDesign {
    url?: string;
    occasion?: string;
    quantity?: string;
    packaging?: string;
    pillowType?: string;
    deliveryDate?: string;
    additionalDetails?: string;
}

export interface IWholesaleProjectDetails {
    occasion?: string;
    quantity?: string;
    pillowType?: string;
    size?:string;
    deliveryDate?: string;
    additionalDetails?: string;
}

interface Props {
    submission: any;
    formData: {
        contact?: IWholesaleContact;
        // legacy shape: per-design metadata
        designs?: IWholesaleDesign[];
        // current shape: shared project details + plain image urls
        designUrls?: string[];
        projectDetails?: IWholesaleProjectDetails;
    };
    typeLabel: string;
}

const clean = (value?: string) => (typeof value === 'string' ? value.trim() : value);

const ContactColumn = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
            {title}
        </Typography>
        {children}
    </Box>
);

const DesignImage = ({ url, index }: { url: string; index: number }) => (
    <Card
        variant="outlined"
        component="a"
        href={url}
        target="_blank"
        rel="noreferrer"
        sx={{
            borderRadius: 2,
            overflow: 'hidden',
            display: 'block',
            textDecoration: 'none',
            transition: 'box-shadow 0.15s',
            '&:hover': { boxShadow: 3 },
        }}
    >
        <Box
            sx={{
                height: 200,
                bgcolor: (t) => t.custom.color.surfaceMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                component="img"
                src={url}
                alt={`Design ${index + 1}`}
                loading="lazy"
                sx={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', p: 1 }}
            />
        </Box>
        <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Design #{index + 1}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Open full image
            </Typography>
        </Box>
    </Card>
);

const LegacyDesignCard = ({ design, index }: { design: IWholesaleDesign; index: number }) => (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
            {design.url ? (
                <Box
                    component="a"
                    href={design.url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                        flexShrink: 0,
                        width: { xs: '100%', md: 280 },
                        height: { xs: 220, md: 'auto' },
                        minHeight: { md: 220 },
                        bgcolor: (t) => t.custom.color.surfaceMuted,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRight: { md: '1px solid' },
                        borderColor: { md: 'divider' },
                    }}
                >
                    <Box
                        component="img"
                        src={design.url}
                        alt={`Design ${index + 1}`}
                        sx={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', p: 1 }}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        flexShrink: 0,
                        width: { xs: '100%', md: 280 },
                        height: 220,
                        bgcolor: (t) => t.custom.color.surfaceMuted,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'text.secondary',
                    }}
                >
                    No image
                </Box>
            )}

            <CardContent sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Design #{index + 1}
                    </Typography>
                    {design.url && (
                        <Typography variant="caption">
                            <Link href={design.url} target="_blank" rel="noreferrer">
                                Open full image
                            </Link>
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                    {design.pillowType && (
                        <Chip label={design.pillowType.trim()} size="small" color="primary" variant="outlined" />
                    )}
                    {design.occasion && (
                        <Chip label={design.occasion.trim()} size="small" variant="outlined" />
                    )}
                    {design.quantity && (
                        <Chip label={`Qty: ${design.quantity.trim()}`} size="small" variant="outlined" />
                    )}
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InfoRow label="Packaging" value={displayValue(design.packaging)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InfoRow label="Delivery Date" value={displayValue(design.deliveryDate)} />
                    </Grid>
                </Grid>

                {design.additionalDetails && (
                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: (t) => t.custom.color.surfaceMuted, borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                            Additional Details
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {design.additionalDetails}
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Box>
    </Card>
);

export default function WholesaleRequestView({ submission, formData, typeLabel }: Props) {
    const contact = formData?.contact || {};
    const legacyDesigns = Array.isArray(formData?.designs) ? formData.designs : [];
    const designUrls = Array.isArray(formData?.designUrls)
        ? formData.designUrls.filter((url): url is string => typeof url === 'string' && url.length > 0)
        : [];
    const projectDetails = formData?.projectDetails;

    const hasAddress = contact.address || contact.apartment || contact.city || contact.state || contact.zip;
    const designsCount = designUrls.length || legacyDesigns.length;

    return (
        <Box sx={{ maxWidth: 1200 }}>
            <SectionCard title="Overview">
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                        <InfoRow label="ID" value={submission.id} />
                        <InfoRow label="Form Type" value={typeLabel} />
                        {submission.orderNumber && (
                            <InfoRow label="Order #" value={`#${submission.orderNumber}`} />
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <InfoRow
                            label="Email"
                            value={
                                submission.email ? (
                                    <Link href={`mailto:${submission.email}`}>
                                        {submission.email}
                                    </Link>
                                ) : '—'
                            }
                        />
                        <InfoRow label="Processed" value={submission.processed ? <StatusChip tone="success" size="sm">Yes</StatusChip> : <StatusChip tone="warning" size="sm">No</StatusChip>} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <InfoRow label="Created" value={formatDate(submission.createdAt)} />
                        <InfoRow label="Updated" value={formatDate(submission.updatedAt)} />
                    </Grid>
                </Grid>
            </SectionCard>

            <SectionCard title="Contact & Shipping">
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <ContactColumn title="Business">
                            <InfoRow label="Company" value={displayValue(contact.companyName)} />
                            <InfoRow label="Full Name" value={displayValue(contact.fullName)} />
                            <InfoRow label="How Heard" value={displayValue(contact.howHeard)} />
                        </ContactColumn>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ContactColumn title="Contact">
                            <InfoRow
                                label="Email"
                                value={
                                    contact.email ? (
                                        <Link href={`mailto:${contact.email}`}>
                                            {contact.email}
                                        </Link>
                                    ) : (
                                        displayValue(contact.email)
                                    )
                                }
                            />
                            <InfoRow
                                label="Phone"
                                value={
                                    contact.phone ? (
                                        <Link href={`tel:${contact.phone}`}>
                                            {contact.phone}
                                        </Link>
                                    ) : (
                                        displayValue(contact.phone)
                                    )
                                }
                            />
                            <InfoRow label="Website" value={displayValue(contact.website)} />
                            <InfoRow label="Best Reach" value={displayValue(contact.bestReach)} />
                        </ContactColumn>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <ContactColumn title="Shipping">
                            <InfoRow label="Type" value={displayValue(contact.shippingType)} />
                            {hasAddress ? (
                                <Box sx={{ mt: 0.5, p: 1.5, bgcolor: (t) => t.custom.color.surfaceMuted, borderRadius: 1, fontSize: '13px', lineHeight: 1.7 }}>
                                    {contact.companyName && <Box sx={{ fontWeight: 600 }}>{contact.companyName}</Box>}
                                    {contact.fullName && <Box>{contact.fullName}</Box>}
                                    {contact.address && <Box>{contact.address}</Box>}
                                    {contact.apartment && <Box>{contact.apartment}</Box>}
                                    {(contact.city || contact.state || contact.zip) && (
                                        <Box>{[contact.city, contact.state, contact.zip].filter(Boolean).join(', ')}</Box>
                                    )}
                                </Box>
                            ) : (
                                <InfoRow label="Address" value={<EmptyValue />} />
                            )}
                        </ContactColumn>
                    </Grid>
                </Grid>
            </SectionCard>

            {projectDetails && (
                <SectionCard title="Project Details">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                        {clean(projectDetails.pillowType) && (
                            <Chip label={clean(projectDetails.pillowType)} size="small" color="primary" variant="outlined" />
                        )}
                        {clean(projectDetails.occasion) && (
                            <Chip label={clean(projectDetails.occasion)} size="small" variant="outlined" />
                        )}
                        {clean(projectDetails.quantity) && (
                            <Chip label={`Qty: ${clean(projectDetails.quantity)}`} size="small" variant="outlined" />
                        )}
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <InfoRow label="Pillow Type" value={displayValue(projectDetails.pillowType)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InfoRow label="Pillow Size" value={displayValue(projectDetails.size)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InfoRow label="Occasion" value={displayValue(projectDetails.occasion)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InfoRow label="Quantity" value={displayValue(projectDetails.quantity)} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <InfoRow label="Delivery Date" value={displayValue(projectDetails.deliveryDate)} />
                        </Grid>
                    </Grid>

                    {clean(projectDetails.additionalDetails) && (
                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: (t) => t.custom.color.surfaceMuted, borderRadius: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Additional Details
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {clean(projectDetails.additionalDetails)}
                            </Typography>
                        </Box>
                    )}
                </SectionCard>
            )}

            <SectionCard title={`Designs (${designsCount})`}>
                {designsCount === 0 ? (
                    <Typography color="text.secondary">No designs submitted</Typography>
                ) : designUrls.length > 0 ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                            gap: 2,
                        }}
                    >
                        {designUrls.map((url, index) => (
                            <DesignImage key={`${url}-${index}`} url={url} index={index} />
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {legacyDesigns.map((design, index) => (
                            <LegacyDesignCard key={index} design={design} index={index} />
                        ))}
                    </Box>
                )}
            </SectionCard>
        </Box>
    );
}
